import { CancelOutlined, CheckCircleOutline, Delete, Visibility } from "@mui/icons-material";
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import { useMemo } from "react";

const ValidationStatusBadge = ({ status }: { status: string }) => {
  const { Icon, color, text } = useMemo(() => {
    const statuses = {
      pending: {
        Icon: CancelOutlined,
        color: "warning",
        text: "Document à vérifier",
      },
      approved: {
        Icon: CheckCircleOutline,
        color: "success",
        text: "Document validé",
      },
      rejected: {
        Icon: CancelOutlined,
        color: "error",
        text: "Document refusé",
      },
    };

    return statuses[status as keyof typeof statuses];
  }, [status]);

  if (!Icon) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      sx={{
        backgroundColor: `${color}.light`,
        padding: "0.125rem 0.5rem",
        borderRadius: "0.5rem",
      }}
    >
      <Icon sx={{ color: `${color}.main`, fontSize: "1.125rem" }} />
      <Typography variant="body1" sx={{ color: `${color}.main`, fontSize: "0.875rem" }}>
        {text}
      </Typography>
    </Stack>
  );
};

type Props = {
  file: any;
  document: any;
  label: string;
  kind: string;
  onDeleteFile: (file: any) => void;
};

const DocumentFileCard = ({ file, document, label, kind, onDeleteFile }: Props) => {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "0.5rem",
        padding: "0.75rem",
        border: "1px solid #E3E7EC",
        flex: "1 1 280px",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <Typography noWrap sx={{ fontSize: "1rem", fontWeight: 600, minWidth: 0 }}>{label}</Typography>
        <Stack direction="row" gap={1} alignItems="center" sx={{ ml: "auto" }}>
          {kind === "free_form" ? (
            <a href={file.url} target="_blank" rel="noopener noreferrer">
              <IconButton sx={{ color: "secondary.main" }}>
                <Visibility />
              </IconButton>
            </a>
          ) : (
            <ValidationStatusBadge status={document.validation_state} />
          )}
          <IconButton onClick={() => onDeleteFile(file)}>
            <Delete sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Stack>
      <Typography variant="body1" noWrap title={file.filename} sx={{ fontSize: "0.875rem" }}>
        {file.filename}
      </Typography>
      <Typography variant="body1" sx={{ fontSize: "0.875rem", color: "grey.500", mt: 1 }}>
        Document importé par {document.uploaded_by === "agent" ? "un agent(e)" : "le candidat"} le{" "}
        {new Date(document.created_at).toLocaleDateString()}
      </Typography>
      {document.approved_at && (
        <Typography variant="body1" sx={{ fontSize: "0.875rem", color: "grey.500" }}>
          Validé le {new Date(document.approved_at).toLocaleDateString()}
        </Typography>
      )}
      {document.rejected_at && (
        <Typography variant="body1" sx={{ fontSize: "0.875rem", color: "grey.500" }}>
          Refusé le {new Date(document.rejected_at).toLocaleDateString()}
        </Typography>
      )}
      {document.rejection_reason && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body1" sx={{ fontSize: "0.875rem", color: "grey.500" }}>
            <b>Motif du refus : </b> <i>{document.rejection_reason}</i>
          </Typography>
        </>
      )}
    </Box>
  );
};

export default DocumentFileCard;
